import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { profileApi } from '../api';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const ROLE_META = {
  SUPER_ADMIN: { color: '#ef4444', label: 'Super Admin', icon: '👑' },
  MANAGER:     { color: '#6366f1', label: 'Manager',     icon: '🧑‍💼' },
  MARKETING:   { color: '#f59e0b', label: 'Marketing',   icon: '📢' },
  SUPPORT:     { color: '#22c55e', label: 'Support',     icon: '🎧' },
};

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { addLog, logs }        = useActivity();

  const [activeTab, setActiveTab] = useState('profile');
  const [saving,    setSaving]    = useState(false);

  const roleMeta = ROLE_META[user?.adminRole] || { color: '#6366f1', label: user?.adminRole, icon: '🧑' };

  const [form, setForm] = useState({
    name:    user?.name    || '',
    mobile:  user?.mobile  || '',
    city:    user?.city    || '',
    state:   user?.state   || '',
    country: user?.country || '',
  });

  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const [showPwd, setShowPwd]   = useState({ c: false, n: false, cf: false });

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  // ── Save profile ─────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name cannot be empty'); return; }
    setSaving(true);
    try {
      await profileApi.update(form);
      updateProfile(form);
      addLog('Profile Updated', user.email, 'update');
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  // ── Change password ───────────────────────────────────────────────────────
  const handlePwdChange = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (!pwdForm.current || !pwdForm.newPwd || !pwdForm.confirm) {
      setPwdError('All fields are required'); return;
    }
    if (pwdForm.newPwd.length < 6) {
      setPwdError('New password must be at least 6 characters'); return;
    }
    if (pwdForm.newPwd !== pwdForm.confirm) {
      setPwdError('Passwords do not match'); return;
    }
    setSaving(true);
    try {
      // Password change goes through the main user API
      const { default: api } = await import('../api/axiosConfig');
      await api.put('/api/auth/change-password', {
        currentPassword: pwdForm.current,
        newPassword: pwdForm.newPwd,
      });
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      addLog('Password Changed', user.email, 'update');
      toast.success('Password changed successfully!');
    } catch (err) {
      setPwdError(err.response?.data?.message || 'Failed to change password (endpoint may not be implemented)');
    } finally { setSaving(false); }
  };

  // ── My recent activity ────────────────────────────────────────────────────
  const myLogs = logs.filter(l => l.admin === user?.name).slice(0, 8);

  const strength = (pwd) =>
    pwd.length === 0 ? null :
    pwd.length < 6   ? { label: 'Weak',   color: '#ef4444', pct: '25%' } :
    pwd.length < 10  ? { label: 'Medium', color: '#f59e0b', pct: '60%' } :
                       { label: 'Strong', color: '#22c55e', pct: '100%' };
  const str = strength(pwdForm.newPwd);

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Account</span> / <span className="current">My Profile</span></div>
          <h1>My Profile</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Manage your admin account details</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', alignItems: 'flex-start' }}>

        {/* ── Left Panel ───────────────────────────────────────────────────── */}
        <div>
          <div style={cardS}>
            {/* Avatar */}
            <div style={{ textAlign: 'center', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)', marginBottom: '16px' }}>
              <div style={{ width: '76px', height: '76px', borderRadius: '50%', margin: '0 auto 12px', background: `linear-gradient(135deg, ${roleMeta.color}, ${roleMeta.color}99)`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', fontWeight: 700, boxShadow: `0 4px 20px ${roleMeta.color}50` }}>
                {user?.profilePic
                  ? <img src={user.profilePic} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : initials}
              </div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>{user?.name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '3px' }}>{user?.email}</div>
              <div style={{ marginTop: '10px' }}>
                <span style={{ fontSize: '0.78rem', background: roleMeta.color + '20', color: roleMeta.color, padding: '4px 12px', borderRadius: '20px', fontWeight: 600 }}>
                  {roleMeta.icon} {roleMeta.label}
                </span>
              </div>
              {user?.city && (
                <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>📍 {user.city}</div>
              )}
            </div>

            {/* Quick stats */}
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '16px' }}>
              {[
                { label: 'Actions', value: myLogs.length },
                { label: 'Session', value: '1' },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--accent-primary)' }}>{s.value}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent activity */}
          <div style={{ ...cardS, marginTop: '16px' }}>
            <h3 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '14px' }}>Recent Actions</h3>
            {myLogs.length === 0 ? (
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>No actions yet this session.</p>
            ) : myLogs.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.9rem' }}>
                  {l.type === 'create' ? '➕' : l.type === 'delete' ? '🗑️' : l.type === 'export' ? '📊' : '✏️'}
                </span>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{l.action}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {new Date(l.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right Panel ──────────────────────────────────────────────────── */}
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            {[
              { key: 'profile',  label: '👤 Edit Profile' },
              { key: 'password', label: '🔒 Password' },
            ].map(t => (
              <button key={t.key}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, background: activeTab === t.key ? 'var(--accent-primary)' : 'var(--bg-tertiary)', color: activeTab === t.key ? 'white' : 'var(--text-secondary)', transition: 'all 0.2s' }}
                onClick={() => setActiveTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div style={cardS}>
              <h3 style={{ fontWeight: 700, marginBottom: '20px' }}>Profile Information</h3>
              <form onSubmit={handleSave}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={fgS}>
                    <label style={labelS}>Full Name *</label>
                    <input style={inputS} value={form.name}    onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your full name" required />
                  </div>
                  <div style={fgS}>
                    <label style={labelS}>Email (read-only)</label>
                    <input style={{ ...inputS, opacity: 0.6 }} value={user?.email || ''} readOnly />
                  </div>
                  <div style={fgS}>
                    <label style={labelS}>Mobile</label>
                    <input style={inputS} value={form.mobile}  onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))} placeholder="10-digit number" />
                  </div>
                  <div style={fgS}>
                    <label style={labelS}>City</label>
                    <input style={inputS} value={form.city}    onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Your city" />
                  </div>
                  <div style={fgS}>
                    <label style={labelS}>State</label>
                    <input style={inputS} value={form.state}   onChange={e => setForm(p => ({ ...p, state: e.target.value }))} placeholder="State" />
                  </div>
                  <div style={fgS}>
                    <label style={labelS}>Country</label>
                    <input style={inputS} value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} placeholder="Country" />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button type="submit" disabled={saving} style={primaryBtnS}>
                    {saving ? 'Saving...' : '✅ Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div style={cardS}>
              <h3 style={{ fontWeight: 700, marginBottom: '20px' }}>Change Password</h3>
              {pwdError && (
                <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '0.875rem', marginBottom: '16px' }}>
                  ⚠️ {pwdError}
                </div>
              )}
              <form onSubmit={handlePwdChange}>
                {[
                  { key: 'c',  label: 'Current Password', field: 'current', val: pwdForm.current },
                  { key: 'n',  label: 'New Password',     field: 'newPwd',  val: pwdForm.newPwd },
                  { key: 'cf', label: 'Confirm Password', field: 'confirm', val: pwdForm.confirm },
                ].map(({ key, label, field, val }) => (
                  <div key={key} style={fgS}>
                    <label style={labelS}>{label}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        style={{ ...inputS, paddingRight: '42px' }}
                        type={showPwd[key] ? 'text' : 'password'}
                        value={val}
                        onChange={e => setPwdForm(p => ({ ...p, [field]: e.target.value }))}
                        placeholder={`Enter ${label.toLowerCase()}`}
                      />
                      <button type="button"
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                        onClick={() => setShowPwd(p => ({ ...p, [key]: !p[key] }))}>
                        {showPwd[key] ? '👁' : '🙈'}
                      </button>
                    </div>
                    {/* Strength meter for new password */}
                    {key === 'n' && str && (
                      <div style={{ marginTop: '6px' }}>
                        <div style={{ height: '4px', background: 'var(--border-color)', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: str.pct, background: str.color, borderRadius: '2px', transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '0.72rem', color: str.color }}>{str.label}</span>
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={saving} style={primaryBtnS}>
                    {saving ? 'Changing...' : '🔒 Change Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const cardS      = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' };
const fgS        = { marginBottom: '14px' };
const labelS     = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const inputS     = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box' };
const primaryBtnS= { padding: '10px 22px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };

export default Profile;
